<template>
  <div class="page-filter">
    <button
      class="page-filter__trigger "
      :class="{ 'page-filter__trigger--disabled': isTriggerDisabled }"
      @click.prevent="openFilter"
    >
      <SvgIconFilter/>

      <span>Filtrar</span>
    </button>

    <BModal
      id="modalPageFilter"
      title="Filtrar"
      :centered="true"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <BaseGrid
        columns="2"
        gap-column="0"
        gap-row="0"
      >
        <!-- name -->
        <BFormGroup
          v-if="isActiveFilter('name')"
          label="Nome"
          label-cols-lg="1"
          id="pageFilterName"
        >
          <BFormInput v-model="filter.name"/>
        </BFormGroup>

        <!-- email -->
        <BFormGroup
          v-if="isActiveFilter('email')"
          label="E-mail"
          label-cols-lg="1"
          id="pageFilterEmail"
        >
          <BFormInput v-model="filter.email"/>
        </BFormGroup>

        <!-- cpf -->
        <BFormGroup
          v-if="isActiveFilter('cpf')"
          label="CPF"
          label-cols-lg="1"
          id="pageFilterCpf"
        >
          <BFormInput
            v-model="filter.cpf"
            v-mask="'###.###.###-##'"
          />
        </BFormGroup>

        <!-- phone -->
        <BFormGroup
          v-if="isActiveFilter('phone')"
          label="Telefone"
          label-cols-lg="1"
          id="pageFilterPhone"
        >
          <BFormInput
            v-model="filter.phone"
            v-mask="'+55 (##) #####-####'"
          />
        </BFormGroup>
      </BaseGrid>

      <!-- dateRange -->
      <BFormGroup
        v-if="isActiveFilter('dateRange')"
        label-cols-lg="1"
        label="Período"
        id="pageFilterDateRange"
      >
        <DateRangePicker
          opens="right"
          :ranges="dateRanges"
          :time-picker="false"
          :show-dropdowns="true"
          :auto-apply="true"
          :locale-data="dateRangePicker.locale"
          v-model="dateRangePicker.model"
          placeholder="Selecione o período de tempo"
          style="width: 100%;"
        >
          <template #input="picker">
            {{ picker.startDate | date('date') }} - {{ picker.endDate | date('date') }}
          </template>
        </DateRangePicker>
      </BFormGroup>

      <!-- action -->
      <BFormGroup
        v-if="isActiveFilter('action')"
        label-cols-lg="1"
        label="Ação"
        id="pageFilterAction"
      >
        <VSelect
          :options="platformActions"
          label="description"
          index="id_action"
          :reduce="action => action.id_action"
          v-model="filter.action"
          placeholder="Selecione o nome da ação (venda, upgrade...)"
          multiple
          @input="fetchPlatformPlugins(filter.action)"
        />
      </BFormGroup>

      <!-- plugin -->
      <BFormGroup
        v-if="isActiveFilter('plugin')"
        label-cols-lg="1"
        label="Origem do lead"
        id="pageFilterPlugin"
      >
        <VSelect
          v-model="filter.plugin"
          :options="platformPlugins"
          :placeholder="pluginPlaceholder"
          :disabled="isPluginDisabled"
          :loading="isLoadingPlatformPlugins"
        />
      </BFormGroup>

      <!-- product -->
      <BFormGroup
        v-if="isActiveFilter('product')"
        label-cols-lg="1"
        label="Produto"
        id="pageFilterProduct"
      >
        <BFormInput
          v-model="filter.product"
          placeholder="Preencha qual produto você quer filtrar"
        />
      </BFormGroup>

      <!-- status -->
      <BFormGroup
        v-if="isActiveFilter('status')"
        label-cols-lg="1"
        label="Status"
        id="pageFilterStatus"
      >
        <VSelect
          :options="leadStatus"
          :reduce="status => status.id"
          v-model="filter.status"
          multiple
          placeholder="Selecione o status deste atendimento (conversão, recusa...)"
        />
      </BFormGroup>

      <BaseGrid
        columns="2"
        gap-column="0"
        gap-row="0"
      >
        <!-- csat -->
        <BFormGroup
          v-if="isActiveFilter('csat')"
          label="Csat"
          label-cols-lg="1"
          id="pageFilterCsat"
        >
          <BaseInputCsat v-model="filter.csat"/>
        </BFormGroup>

        <!-- sentimento -->
        <BFormGroup
          v-if="isActiveFilter('feeling')"
          label="Sentimento"
          label-cols-lg="1"
          id="pageFilterFeeling"
        >
          <BaseInputFeeling v-model="filter.feeling"/>
        </BFormGroup>
      </BaseGrid>

      <div class="page-filter__buttons">
        <BaseButton
          id="pageFilterReset"
          @click.native="resetFilter"
          skin="secondary"
        >
          Limpar
        </BaseButton>

        <BaseButton
          id="pageFilterApply"
          @click.native="applyFilter"
        >
          Filtrar
        </BaseButton>
      </div>
    </BModal>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { dateFilter } from '@/utils/filters'
import { pageFilterExceptionMixin } from '@/utils/mixins'
import BaseGrid from '@/components/base/BaseGrid'
import BaseButton from '@/components/base/BaseButton'
import BaseInputCsat from '@/components/base/BaseInputCsat'
import BaseInputFeeling from '@/components/base/BaseInputFeeling'
import DateRangePicker from 'vue2-daterange-picker'
import VSelect from 'vue-select'
import dayjs from 'dayjs'

export default {
  name: 'CommonPageFilter',

  components: {
    BaseGrid,
    BaseButton,
    BaseInputCsat,
    BaseInputFeeling,
    DateRangePicker,
    VSelect
  },

  filters: {
    date: dateFilter
  },

  mixins: [
    pageFilterExceptionMixin
  ],

  props: {
    filters: {
      type: Array,
      validator (value) {
        const filters = [
          'name',
          'email',
          'phone',
          'cpf',
          'dateRange',
          'action',
          'plugin',
          'product',
          'status',
          'csat',
          'feeling'
        ]

        return value.every(filter => filters.includes(filter))
      }
    }
  },

  data () {
    return {
      filter: {
        name: '',
        email: '',
        phone: '',
        cpf: '',
        dateRange: '',
        action: '',
        plugin: '',
        product: '',
        status: '',
        csat: [],
        feeling: []
      },
      dateRangePicker: {
        model: {
          startDate: null,
          endDate: null
        },
        locale: {
          daysOfWeek: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
          monthNames: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
        }
      }
    }
  },

  computed: {
    ...mapState('base', [
      'leadStatus',
      'platformActions',
      'platformPlugins',
      'isLoadingPlatformPlugins'
    ]),

    isTriggerDisabled () {
      return this.isActiveFilter('action')
        ? !this.platformActions
        : false
    },

    dateRanges () {
      const now = dayjs()
      const today = now.$d
      const yesterday = now.subtract(1, 'day').$d
      const last7Days = now.subtract(7, 'day').$d
      const last15Days = now.subtract(15, 'day').$d
      const last30Days = now.subtract(30, 'day').$d
      const MonthCurrentStart = now.startOf('month').$d
      const MonthCurrentEnd = now.endOf('month').$d
      const MonthLastStart = now.subtract(1, 'month').startOf('month').$d
      const MonthLastEnd = now.subtract(1, 'month').endOf('month').$d

      return {
        'Hoje': [today, today],
        'Ontem': [yesterday, yesterday],
        'Mês atual': [MonthCurrentStart, MonthCurrentEnd],
        'Mês passado': [MonthLastStart, MonthLastEnd],
        'Últimos 7 dias': [last7Days, today],
        'Últimos 15 dias': [last15Days, today],
        'Últimos 30 dias': [last30Days, today],
      }
    },

    pluginPlaceholder () {
      if (!this.filter.action.length) {
        return 'Selecione uma ação para visualizar a origem do lead'
      }

      if (this.isLoadingPlatformPlugins) {
        return 'Procurando origens...'
      }

      if (!this.platformPlugins.length) {
        if (this.filter.action.length === 1) {
          return 'Nenhuma origem para a ação selecionada'
        } else {
          return 'Nenhuma origem para as ações selecionadas'
        }
      }

      return 'Selecione uma origem'
    },

    isPluginDisabled () {
      return this.isLoadingPlatformPlugins
        || !this.platformPlugins.length
        || !this.filter.action.length
    }
  },

  watch: {
    'filter.action' (value) {
      if (!value?.length) {
        this.filter.plugin = ''
      }
    },

    platformPlugins () {
      this.filter.plugin = ''
    }
  },

  created () {
    if (!this.pageFilter.dateRange) {
      this.resetPageFilter()
    }

    this.fillFilter()
    this.applyFilter()

    if (this.isActiveFilter('action')) {
      this.fetchPlatformActions()
    }
  },

  mounted () {
    this.$root.$on('open-page-filter', this.openFilter)
  },

  deactivated () {
    this.setShowPageFilterBar(false)
  },

  activated () {
    this.setShowPageFilterBar(true)
  },

  methods: {
    ...mapMutations('base', {
      setPageFilter: 'SET_PAGE_FILTER',
      setShowPageFilterBar: 'SET_SHOW_PAGE_FILTER_BAR'
    }),

    ...mapActions('base', [
      'resetPageFilter',
      'fetchPlatformActions',
      'fetchPlatformPlugins'
    ]),

    isActiveFilter (filter) {
      return this.filters.includes(filter)
    },

    fillFilter () {
      const [dateStart, dateEnd] = this.pageFilter.dateRange
      let dateRangePickerModel = {
        startDate: dateStart ? dayjs(dateStart) : null,
        endDate: dateEnd ? dayjs(dateEnd) : null
      }

      Object.assign(this.dateRangePicker.model, dateRangePickerModel)

      Object.keys(this.filter).forEach(filter => {
        const filtersArrayType = [
          'csat',
          'feeling'
        ]
        const valueDefault = filtersArrayType.includes(filter) ? [] : ''

        this.filter[filter] = this.isActiveFilter(filter) ? this.pageFilter[filter] : valueDefault
      })
    },

    resetFilter () {
      this.resetPageFilter()
      this.fillFilter()
      this.applyFilter()
    },

    openFilter () {
      this.fillFilter()

      this.$bvModal.show('modalPageFilter')
    },

    closeFilter () {
      this.$bvModal.hide('modalPageFilter')
    },

    applyFilter () {
      this.pruneFilterDateRange()
      this.pruneFilterAction()
      this.pruneFilterPlugin()
      this.pruneFilterPhone()
      this.pruneFilterCpf()
      this.setIsPageFilterExceptionCsat(false)
      this.setIsPageFilterExceptionFeeling(false)
      this.setIsPageFilterExceptionStatus(false)
      this.setPageFilter(this.filter)
      this.closeFilter()
    },

    pruneFilterDateRange () {
      const { startDate, endDate } = this.dateRangePicker.model

      this.filter.dateRange = !startDate || !endDate
        ? ''
        : [
            dateFilter(startDate, 'base'),
            dateFilter(endDate, 'base')
          ]
    },

    pruneFilterAction () {
      const isEmpty = !this.filter.action?.length

      if (isEmpty) {
        this.filter.action = ''
      }
    },

    pruneFilterPlugin () {
      const isEmpty = !this.filter.plugin?.length

      if (isEmpty) {
        this.filter.plugin = ''
      }
    },

    pruneFilterPhone () {
      const { phone: filter } = this.filter

      if (filter) {
        this.filter.phone = filter.replace(/[\s|\(|\)|\-]/g, '')
      }
    },

    pruneFilterCpf () {
      const { cpf: filter } = this.filter

      if (filter) {
        this.filter.cpf = filter.replace(/[\.|\-]/g, '')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-filter {
  $button-height: map-get($layout-insights, 'button-height');

  &__trigger {
    display: flex;
    height: $button-height;
    align-items: center;
    gap: 10px;
    padding: 0 30px;
    border: 2px solid nth($colors-custom, 1);
    border-radius: 2em;
    transition: all 0.1s;
    background: none;

    &--disabled {
      @extend %disabled;
    }

    &:hover {
      border-color: nth($colors-custom, 2);
      background-color: nth($colors-custom, 2);
    }

    &:active {
      border-color: nth($colors-custom, 1);
      background-color: nth($colors-custom, 1);

      span {
        color: #fff;
      }

      svg {
        fill: #fff;
      }
    }

    span {
      transition: all 0.1s;
      font: {
        weight: 500;
        size: 13px;
      }
      white-space: nowrap;
    }

    svg {
      width: 12px;
      height: 12px;
      transition: all 0.1s;
      fill: nth($colors-custom, 1);
    }
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 15px;

    /deep/ {
      .button {
        &:not(:first-child) {
          margin-left: 10px;
        }
      }
    }
  }
}

/deep/ {
  input,
  .reportrange-text {
    font: {
      weight: 500;
      size: 15px;
      family: $font-default;
    }

    &::placeholder {
      color: rgba(nth($colors-custom, 1), 0.4);
      font-weight: 300;
    }
  }

  .form-row {
    legend {
      max-width: 130px;
      flex-basis: 130px;
      font: {
        weight: 300;
        size: 15px;
        family: $font-default;
      }
      text-align: right;
    }
  }
}
</style>
